import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { 
  TrendingUp, DollarSign, ShoppingCart, Package, AlertTriangle,
  ArrowUp, ArrowDown, Calendar, RefreshCw, ChevronRight, Eye
} from 'lucide-react';

export default function SalesDashboard() {
  const { token } = useAuth();
  const { currentColors } = useTheme();
  
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('daily');
  const [days, setDays] = useState(30);
  const [periodData, setPeriodData] = useState([]);

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    loadPeriodData();
  }, [period, days]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/sales/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPeriodData = async () => {
    try {
      const response = await axios.get(`${API}/admin/sales/by-period?period=${period}&days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPeriodData(response.data.data);
    } catch (error) {
      console.error('Failed to load period data:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  // Calculate chart scale
  const maxRevenue = Math.max(...periodData.map(d => d.revenue), 1);
  const maxProfit = Math.max(...periodData.map(d => d.profit), 1);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-12 h-12 animate-spin" style={{ color: currentColors.primary }} />
      </div>
    );
  }

  const summary = dashboard?.summary || {};

  return (
    <div className="space-y-6" data-testid="sales-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-8 h-8" style={{ color: currentColors.primary }} />
          <h1 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>
            Sales Dashboard
          </h1>
        </div>
        <button
          onClick={loadDashboard}
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
          style={{ backgroundColor: `${currentColors.primary}20`, color: currentColors.primary }}
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Revenue */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl" style={{ backgroundColor: `${currentColors.primary}20` }}>
              <DollarSign className="w-6 h-6" style={{ color: currentColors.primary }} />
            </div>
          </div>
          <p className="text-sm mb-1" style={{ color: currentColors.textMuted }}>Total Revenue</p>
          <p className="text-2xl font-bold" style={{ color: currentColors.text }}>
            {formatCurrency(summary.total_revenue)}
          </p>
        </div>

        {/* Total Profit */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-green-500/20">
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
          </div>
          <p className="text-sm mb-1" style={{ color: currentColors.textMuted }}>Total Profit</p>
          <p className="text-2xl font-bold text-green-400">
            {formatCurrency(summary.total_profit)}
          </p>
          <p className="text-xs mt-1" style={{ color: currentColors.textMuted }}>
            {summary.profit_margin?.toFixed(1)}% margin
          </p>
        </div>

        {/* Total Orders */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl" style={{ backgroundColor: `${currentColors.secondary}20` }}>
              <ShoppingCart className="w-6 h-6" style={{ color: currentColors.secondary }} />
            </div>
          </div>
          <p className="text-sm mb-1" style={{ color: currentColors.textMuted }}>Total Orders</p>
          <p className="text-2xl font-bold" style={{ color: currentColors.text }}>
            {formatNumber(summary.total_orders)}
          </p>
          <p className="text-xs mt-1" style={{ color: currentColors.textMuted }}>
            Avg: {formatCurrency(summary.average_order_value)}
          </p>
        </div>

        {/* Items Sold */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-purple-500/20">
              <Package className="w-6 h-6 text-purple-400" />
            </div>
          </div>
          <p className="text-sm mb-1" style={{ color: currentColors.textMuted }}>Items Sold</p>
          <p className="text-2xl font-bold" style={{ color: currentColors.text }}>
            {formatNumber(summary.total_items_sold)}
          </p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Chart */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-cinzel" style={{ color: currentColors.primary }}>Sales Trend</h2>
            <div className="flex gap-2">
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="cosmic-input text-sm py-1"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="cosmic-input text-sm py-1"
              >
                <option value="7">7 days</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
              </select>
            </div>
          </div>
          
          {periodData.length > 0 ? (
            <div className="h-64 flex items-end gap-1">
              {periodData.slice(-14).map((day, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full rounded-t transition-all hover:opacity-80 relative group"
                    style={{ 
                      height: `${(day.revenue / maxRevenue) * 200}px`,
                      backgroundColor: currentColors.primary,
                      minHeight: day.revenue > 0 ? '4px' : '0'
                    }}
                  >
                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block z-10">
                      <div className="bg-black/80 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                        Revenue: {formatCurrency(day.revenue)}<br/>
                        Profit: {formatCurrency(day.profit)}<br/>
                        Orders: {day.orders}
                      </div>
                    </div>
                  </div>
                  <p className="text-[10px] mt-1 truncate w-full text-center" style={{ color: currentColors.textMuted }}>
                    {day.date.split('-').slice(1).join('/')}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center" style={{ color: currentColors.textMuted }}>
              No sales data for this period
            </div>
          )}

          <div className="flex justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: currentColors.primary }}></div>
              <span className="text-xs" style={{ color: currentColors.textMuted }}>Revenue</span>
            </div>
          </div>
        </div>

        {/* Top Products */}
        <div className="glass-card p-6">
          <h2 className="font-cinzel mb-6" style={{ color: currentColors.primary }}>Top Products</h2>
          
          {dashboard?.top_products?.length > 0 ? (
            <div className="space-y-4">
              {dashboard.top_products.map((product, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-4 p-3 rounded-lg transition-all hover:bg-white/5"
                  style={{ backgroundColor: `${currentColors.primary}05` }}
                >
                  <span 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                    style={{ 
                      backgroundColor: idx === 0 ? '#FFD700' : idx === 1 ? '#C0C0C0' : idx === 2 ? '#CD7F32' : `${currentColors.primary}20`,
                      color: idx < 3 ? '#000' : currentColors.primary
                    }}
                  >
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate" style={{ color: currentColors.text }}>
                      {product.product_name}
                    </p>
                    <p className="text-xs" style={{ color: currentColors.textMuted }}>
                      SKU: {product.sku} • {product.quantity_sold} sold
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium" style={{ color: currentColors.primary }}>
                      {formatCurrency(product.revenue)}
                    </p>
                    <p className="text-xs text-green-400">
                      +{formatCurrency(product.profit)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center" style={{ color: currentColors.textMuted }}>
              No sales data yet
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Order Status */}
        <div className="glass-card p-6">
          <h2 className="font-cinzel mb-4" style={{ color: currentColors.primary }}>Order Status</h2>
          <div className="space-y-3">
            {Object.entries(dashboard?.order_status_counts || {}).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className="capitalize text-sm" style={{ color: currentColors.text }}>{status}</span>
                <span 
                  className={`px-2 py-1 rounded-full text-xs ${
                    status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                    status === 'processing' ? 'bg-blue-500/20 text-blue-400' :
                    status === 'shipped' ? 'bg-purple-500/20 text-purple-400' :
                    status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                    'bg-red-500/20 text-red-400'
                  }`}
                >
                  {count}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Low Stock Alerts */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-cinzel" style={{ color: currentColors.primary }}>Low Stock Alerts</h2>
            {dashboard?.low_stock_count > 0 && (
              <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">
                {dashboard.low_stock_count} items
              </span>
            )}
          </div>
          
          {dashboard?.low_stock_products?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.low_stock_products.map((product, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-2 rounded-lg bg-red-500/5"
                >
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span className="text-sm truncate" style={{ color: currentColors.text }}>
                      {product.name}
                    </span>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">
                    {product.stock_quantity} left
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center" style={{ color: currentColors.textMuted }}>
              <div className="text-center">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">All products well stocked</p>
              </div>
            </div>
          )}
        </div>

        {/* Recent Orders */}
        <div className="glass-card p-6">
          <h2 className="font-cinzel mb-4" style={{ color: currentColors.primary }}>Recent Orders</h2>
          
          {dashboard?.recent_orders?.length > 0 ? (
            <div className="space-y-3">
              {dashboard.recent_orders.map((order, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between p-2 rounded-lg transition-all hover:bg-white/5"
                  style={{ backgroundColor: `${currentColors.primary}05` }}
                >
                  <div>
                    <p className="text-sm font-medium" style={{ color: currentColors.text }}>
                      {order.order_number}
                    </p>
                    <p className="text-xs" style={{ color: currentColors.textMuted }}>
                      {order.customer_name}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium" style={{ color: currentColors.primary }}>
                      {formatCurrency(order.total)}
                    </p>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                      order.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center" style={{ color: currentColors.textMuted }}>
              <div className="text-center">
                <ShoppingCart className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No orders yet</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cost & Profit Breakdown */}
      <div className="glass-card p-6">
        <h2 className="font-cinzel mb-6" style={{ color: currentColors.primary }}>Financial Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center p-4 rounded-xl" style={{ backgroundColor: `${currentColors.primary}10` }}>
            <p className="text-sm mb-2" style={{ color: currentColors.textMuted }}>Total Revenue</p>
            <p className="text-xl font-bold" style={{ color: currentColors.primary }}>
              {formatCurrency(summary.total_revenue)}
            </p>
          </div>
          <div className="text-center p-4 rounded-xl bg-red-500/10">
            <p className="text-sm mb-2" style={{ color: currentColors.textMuted }}>Total Cost</p>
            <p className="text-xl font-bold text-red-400">
              -{formatCurrency(summary.total_cost)}
            </p>
          </div>
          <div className="text-center p-4 rounded-xl bg-green-500/10">
            <p className="text-sm mb-2" style={{ color: currentColors.textMuted }}>Net Profit</p>
            <p className="text-xl font-bold text-green-400">
              {formatCurrency(summary.total_profit)}
            </p>
          </div>
          <div className="text-center p-4 rounded-xl" style={{ backgroundColor: `${currentColors.secondary}10` }}>
            <p className="text-sm mb-2" style={{ color: currentColors.textMuted }}>Profit Margin</p>
            <p className="text-xl font-bold" style={{ color: currentColors.secondary }}>
              {summary.profit_margin?.toFixed(1) || 0}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
